/**
 * The Pro card's code row (subscription R19), and the fact line under it.
 *
 * Approved spec of 21 October 2026. Every decision is `model/trialCode`'s — which face, which words,
 * what the checkout carries — and every piece of state is `lib/useTrialCode`'s; this file lays the
 * faces out in two rows. The CONTROL row is one control tall in every face — „Masz kod?", the
 * field, „Kod PGB · Usuń" — and the FACT row under it is the card's own promo line, which holds
 * the launch offer, the trial's sentence, the month's note or a refused code's reason, and keeps
 * the two lines the longest of them takes. Opening the field, applying a code and a refusal coming
 * back change ink inside the rows and never move the seats line or the button under them.
 *
 * ── No pill, no fill ────────────────────────────────────────────────────────────────────────
 * An applied code is plain text — „Kod PGB" and an inline „Usuń". The ember stays where the card
 * already spends it: the fact line, which is the one sentence that says what the code changed.
 */

import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { FormField } from '~/shared/ui/FormField';
import ghost from '~/shared/ui/ghostButton.module.css';
import inlineText from '~/shared/ui/inlineTextButton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { TrialCodeApi } from '../lib/useTrialCode';
import { TRIAL_CODE_MAX, type TrialCodeCopy, type TrialCodeFace } from '../model/trialCode';
import styles from './PlanPicker.module.css';

interface TrialCodeRowProps {
  readonly face: TrialCodeFace;
  readonly copy: TrialCodeCopy | null;
  readonly trial: TrialCodeApi;
  /** Whether the launch-offer line belongs under the price — the card's own answer, unchanged. */
  readonly promoted: boolean;
  /** The card is leaving for the payment page; every control here refuses beside it. */
  readonly busy: boolean;
  readonly onToAnnual: () => void;
}

/** The launch offer, on the yearly face only. Every card draws it from here, with or without a row. */
export function PromoLine() {
  const { t } = useTranslation();
  return (
    <p className={styles.promo}>
      {/* One line, one colour, one element — the lead is no longer a mark on a sentence in a
        * different ink (owner, 22 September 2026). Both halves stay separate KEYS because they
        * are separate sentences in Polish and English alike. */}
      {t('web:account.subscription.picker.promoLead')}{' '}
      {t('web:account.subscription.picker.promoRest')}
    </p>
  );
}

export function TrialCodeRow({ face, copy, trial, promoted, busy, onToAnnual }: TrialCodeRowProps) {
  const { t } = useTranslation();
  const { toggleRef, fieldRef, removeRef } = trial;
  const field = face.kind === 'collapsed' && trial.editing;
  const codeOnCard = face.kind === 'applied' || face.kind === 'annualOnly';
  const refused = field && trial.error !== null;
  const reasonId = useId();

  return (
    <div className={styles.trial} aria-busy={busy}>
      <div className={styles.trialControl}>
        {face.kind === 'collapsed' && !trial.editing && (
          /* In a line of its own rather than wearing the line's class: `inlineTextButton` owns the
           * button's ink, and a second `color` on the same element would be settled by chunk order. */
          <p className={styles.trialText}>
            <button
              ref={toggleRef}
              type="button"
              className={inlineText.inlineTextButton}
              aria-disabled={busy}
              onClick={() => {
                if (!busy) trial.onOpen();
              }}
            >
              {t('web:account.subscription.trial.toggle')}
            </button>
          </p>
        )}

        {field && (
          <FormField
            label={t('web:account.subscription.trial.label')}
            value={trial.draft}
            onChange={trial.onDraft}
            autoComplete="off"
            spellCheck={false}
            maxLength={TRIAL_CODE_MAX}
            inputRef={fieldRef}
            inline
            placeholder={t('web:account.subscription.trial.label')}
            invalid={refused}
            describedBy={refused ? reasonId : undefined}
            onKeyDown={(event) => {
              // Enter applies the code and never reaches the checkout: the card's button is the one
              // control that leaves the page, and a key in a text field must not press it. While
              // the card is leaving it does nothing, exactly as „Zastosuj" beside it.
              if (event.key === 'Enter') {
                event.preventDefault();
                if (!busy) trial.onApply();
              } else if (event.key === 'Escape') {
                event.preventDefault();
                trial.onEscape();
              }
            }}
            trailing={
              <button
                type="button"
                className={`${String(ghost.ghost)} ${String(styles.trialApply)}`}
                aria-disabled={trial.draft === '' || busy}
                onClick={() => {
                  if (trial.draft !== '' && !busy) trial.onApply();
                }}
              >
                {t('web:account.subscription.trial.apply')}
              </button>
            }
          />
        )}

        {codeOnCard && trial.applied !== null && (
          <p className={styles.trialText}>
            {t('web:account.subscription.trial.appliedLead')}{' '}
            <b className={styles.trialCode}>{trial.applied}</b>{' '}
            <button
              ref={removeRef}
              type="button"
              className={inlineText.inlineTextButton}
              aria-disabled={busy}
              onClick={() => {
                if (!busy) trial.onRemove();
              }}
            >
              {t('web:account.subscription.trial.remove')}
              {/* „Usuń" alone names no object for somebody reaching it by Tab. The visible word
                * leads the name, so voice control still finds it by what it says. */}
              <span className={hidden.srOnly}>
                {t('web:account.subscription.trial.removeTail', { code: trial.applied })}
              </span>
            </button>
          </p>
        )}
      </div>

      {/* The fact row: the card's promo line, and the one place every face's sentence stands. A
        * refused code's reason replaces the line rather than doubling it, and the field names it
        * as its description. */}
      <div className={styles.trialFact}>
        {refused ? (
          <p id={reasonId} className={styles.trialError}>
            {trial.error}
          </p>
        ) : copy !== null ? (
          <p className={face.kind === 'annualOnly' ? styles.trialNote : styles.promo}>
            {copy.fact}
            {face.kind === 'annualOnly' && (
              <>
                {' '}
                <button
                  type="button"
                  className={inlineText.inlineTextButton}
                  aria-disabled={busy}
                  onClick={() => {
                    if (!busy) onToAnnual();
                  }}
                >
                  {t('web:account.subscription.trial.toAnnual')}
                </button>
              </>
            )}
          </p>
        ) : (
          promoted && <PromoLine />
        )}
      </div>
    </div>
  );
}
